import React, { useState, useEffect, useRef } from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  PointElement,
  LineElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  RadialLinearScale,
  Filler
} from 'chart.js';
import { Bar, Line, Pie, Doughnut, PolarArea } from 'react-chartjs-2';
import { 
  TrendingUp, 
  Users, 
  CreditCard, 
  Calendar, 
  FileText, 
  RefreshCw,
  DollarSign,
  Shield,
  Activity,
  BarChart3,
  Database,
  Eye,
  ChevronRight,
  Download,
  X,
  Filter,
  ChevronDown,
  Wallet // Changed from DollarSign to Wallet
} from 'lucide-react';
import api from '../../Services/api';
import * as XLSX from 'xlsx';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  PointElement,
  LineElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  RadialLinearScale,
  Filler
);

const Dashboard = () => {
  // State for dashboard metrics
  const [dashboardData, setDashboardData] = useState({
    outstandingBalance: [],
    paymentHistory: [],
    expiringLicenses: [],
    businessLogs: [],
    clients: []
  });
  
  const [loading, setLoading] = useState({
    outstanding: false,
    payments: false,
    expiring: false,
    logs: false,
    clients: false
  });

  const [selectedChart, setSelectedChart] = useState(null);
  const [chartViewData, setChartViewData] = useState([]);
  const [chartViewTitle, setChartViewTitle] = useState('');
  const [showChartDetail, setShowChartDetail] = useState(false);
  const [refreshCount, setRefreshCount] = useState(0);
  const [isExporting, setIsExporting] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  // Refs for chart click handlers
  const chartRefs = {
    outstandingChart: useRef(null),
    paymentsChart: useRef(null),
    licenseChart: useRef(null),
    interactionsChart: useRef(null)
  };

  // Format currency
  const formatCurrency = (amount) => {
    const num = parseFloat(amount) || 0;
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'LKR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 2
    }).format(num);
  };

  // Format date
  const formatDate = (dateString) => {
    if (!dateString || dateString === 'null' || dateString === null) return 'N/A';
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return 'N/A';
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    } catch (error) {
      return 'N/A';
    }
  };

  // Calculate days until expiry
  const getDaysUntilExpiry = (expiryDate) => {
    if (!expiryDate || expiryDate === 'null' || expiryDate === null) return -1;
    try {
      const expiry = new Date(expiryDate);
      if (isNaN(expiry.getTime())) return -1;
      const today = new Date();
      const diffTime = expiry - today;
      return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    } catch (error) {
      return -1;
    }
  };

  // Function to load all dashboard data
  const loadDashboardData = async () => {
    try {
      setLoading({
        outstanding: true,
        payments: true,
        expiring: true,
        logs: true,
        clients: true
      });

      console.log('Loading dashboard data from APIs...');

      const apiCalls = [
        api.get('/Licenses/GetOutStandingBalance').then(response => {
          console.log('Outstanding Balance API Response:', response.data);
          if (response.data?.StatusCode === 200 && response.data.ResultSet) {
            const filteredData = response.data.ResultSet.filter(item => {
              const balance = parseFloat(item.spm_balance_amount || item.balance_amount) || 0;
              return balance > 0;
            });
            console.log(`Loaded ${filteredData.length} outstanding records`);
            return { type: 'outstanding', data: filteredData };
          }
          console.warn('No outstanding balance data found');
          return { type: 'outstanding', data: [] };
        }).catch(error => {
          console.error('Error loading outstanding balance:', error);
          return { type: 'outstanding', data: [] };
        }),

        api.get('/Licenses/GetPaymentHistory').then(response => {
          console.log('Payment History API Response:', response.data);
          if (response.data?.StatusCode === 200 && response.data.ResultSet) {
            return { type: 'payments', data: response.data.ResultSet };
          }
          console.warn('No payment history data found');
          return { type: 'payments', data: [] };
        }).catch(error => {
          console.error('Error loading payment history:', error);
          return { type: 'payments', data: [] };
        }),

        api.get('/Licenses/GetLicenseExpiring').then(response => {
          console.log('Expiring Licenses API Response:', response.data);
          if (response.data?.StatusCode === 200 && response.data.ResultSet) {
            return { type: 'expiring', data: response.data.ResultSet };
          }
          console.warn('No expiring licenses data found');
          return { type: 'expiring', data: [] };
        }).catch(error => {
          console.error('Error loading expiring licenses:', error);
          return { type: 'expiring', data: [] };
        }),

        api.get('/Client/GetBusinessInteractionLog').then(response => {
          console.log('Business Logs API Response:', response.data);
          if (response.data?.StatusCode === 200 && response.data.ResultSet) {
            return { type: 'logs', data: response.data.ResultSet };
          }
          console.warn('No business logs data found');
          return { type: 'logs', data: [] };
        }).catch(error => {
          console.error('Error loading business logs:', error);
          return { type: 'logs', data: [] };
        }),

        api.get('/Client/GetAllClient').then(response => {
          console.log('Clients API Response:', response.data);
          if (response.data?.StatusCode === 200 && response.data.ResultSet) {
            return { type: 'clients', data: response.data.ResultSet };
          }
          console.warn('No clients data found');
          return { type: 'clients', data: [] };
        }).catch(error => {
          console.error('Error loading clients:', error);
          return { type: 'clients', data: [] };
        })
      ];

      const results = await Promise.all(apiCalls);
      
      const newData = {
        outstandingBalance: [],
        paymentHistory: [],
        expiringLicenses: [],
        businessLogs: [],
        clients: []
      };

      results.forEach(result => {
        switch (result.type) {
          case 'outstanding':
            newData.outstandingBalance = result.data || [];
            break;
          case 'payments':
            newData.paymentHistory = result.data || [];
            break;
          case 'expiring':
            newData.expiringLicenses = result.data || [];
            break;
          case 'logs':
            newData.businessLogs = result.data || [];
            break;
          case 'clients':
            newData.clients = result.data || [];
            break;
        }
      });

      setDashboardData(newData);
      setRefreshCount(prev => prev + 1);
      console.log('Dashboard data updated successfully');

    } catch (error) {
      console.error('Error loading dashboard data:', error);
    } finally {
      setLoading({
        outstanding: false,
        payments: false,
        expiring: false,
        logs: false,
        clients: false
      });
    }
  };

  // Load data on component mount
  useEffect(() => {
    loadDashboardData();
  }, []);

  // Calculate stats from data
  const calculateStats = () => {
    const outstandingAmount = dashboardData.outstandingBalance.reduce((sum, item) => {
      const balance = parseFloat(item.spm_balance_amount || item.balance_amount) || 0;
      return sum + (balance > 0 ? balance : 0);
    }, 0);
    
    const totalPayments = dashboardData.paymentHistory.reduce((sum, item) => 
      sum + (parseFloat(item.spd_amount || item.amount || item.paid_amount) || 0), 0);
    
    const currentMonth = new Date().getMonth();
    const currentYear = new Date().getFullYear();
    
    const activePayingLicenses = dashboardData.paymentHistory.filter(payment => {
      const paymentDate = payment.spd_payment_date || payment.payment_date || payment.created_date;
      if (!paymentDate || paymentDate === 'null') return false;
      
      try {
        const date = new Date(paymentDate);
        if (isNaN(date.getTime())) return false;
        return date.getMonth() === currentMonth && date.getFullYear() === currentYear;
      } catch (error) {
        return false;
      }
    }).length;
    
    const expiringSoon = dashboardData.expiringLicenses.filter(license => {
      const days = getDaysUntilExpiry(license.slm_expiry_date || license.expiry_date);
      return days > 0 && days <= 30;
    }).length;
    
    const expiredLicenses = dashboardData.expiringLicenses.filter(license => {
      const days = getDaysUntilExpiry(license.slm_expiry_date || license.expiry_date);
      return days <= 0 && days !== -1;
    }).length;
    
    const activeLicenses = dashboardData.expiringLicenses.filter(license => {
      const days = getDaysUntilExpiry(license.slm_expiry_date || license.expiry_date);
      return days > 0;
    }).length;

    const urgentLicenses = dashboardData.expiringLicenses.filter(license => {
      const days = getDaysUntilExpiry(license.slm_expiry_date || license.expiry_date);
      return days > 0 && days <= 7;
    }).length;

    const totalClients = dashboardData.clients.length;

    return {
      outstandingAmount,
      totalPayments,
      activePayingLicenses,
      expiringSoon,
      expiredLicenses,
      activeLicenses,
      urgentLicenses,
      totalLicenses: dashboardData.expiringLicenses.length,
      totalClients,
      totalLogs: dashboardData.businessLogs.length
    };
  };

  const stats = calculateStats();

  // Prepare chart data
  const prepareChartData = () => {
    // 1. Outstanding Balance by Client
    const outstandingByClient = {};
    dashboardData.outstandingBalance.forEach(item => {
      const clientName = item.scm_client_name || item.client_name || 'Unknown Client';
      const balance = parseFloat(item.spm_balance_amount || item.balance_amount) || 0;
      
      if (balance > 0) {
        if (outstandingByClient[clientName]) {
          outstandingByClient[clientName] += balance;
        } else {
          outstandingByClient[clientName] = balance;
        }
      }
    });

    const topOutstanding = Object.entries(outstandingByClient)
      .sort((a, b) => b[1] - a[1])
      .slice(0, Math.min(8, Object.keys(outstandingByClient).length));

    // 2. Monthly Payment Trend
    const monthlyRevenue = {};
    const last6Months = [];
    const now = new Date();
    
    for (let i = 5; i >= 0; i--) {
      const date = new Date(now.getFullYear(), now.getMonth() - i, 1);
      last6Months.push(date.toLocaleString('en-US', { month: 'short', year: '2-digit' }));
    }

    last6Months.forEach(month => {
      monthlyRevenue[month] = 0;
    });

    dashboardData.paymentHistory.forEach(payment => {
      const paymentDate = payment.spd_payment_date || payment.payment_date || payment.created_date;
      if (paymentDate && paymentDate !== 'null') {
        try {
          const date = new Date(paymentDate);
          if (!isNaN(date.getTime())) {
            const month = date.toLocaleString('en-US', { month: 'short', year: '2-digit' });
            const amount = parseFloat(payment.spd_amount || payment.amount || payment.paid_amount) || 0;
            
            if (monthlyRevenue[month] !== undefined) {
              monthlyRevenue[month] += amount;
            }
          }
        } catch (error) {
          console.warn('Invalid date format:', paymentDate);
        }
      }
    });

    // 3. License Status Distribution
    const licenseStatusData = {
      labels: ['Urgent (≤7 days)', 'Expiring Soon (≤30 days)', 'Expired'],
      datasets: [
        {
          data: [
            stats.urgentLicenses,
            stats.expiringSoon,
            Math.max(0, stats.activeLicenses - stats.expiringSoon - stats.urgentLicenses),
            stats.expiredLicenses
          ],
          backgroundColor: [
            '#EF4444',
            '#F59E0B',
            '#6B7280'
          ],
          borderWidth: 2,
          borderColor: '#FFFFFF',
          hoverOffset: 15
        },
      ],
    };

    // 4. Business Interaction Types
    const interactionTypes = {};
    dashboardData.businessLogs.forEach(log => {
      const type = log.sbl_log_type || log.log_type || 'General';
      interactionTypes[type] = (interactionTypes[type] || 0) + 1;
    });

    const interactionTypesArray = Object.entries(interactionTypes)
      .sort((a, b) => b[1] - a[1]);

    return {
      outstandingChart: {
        type: 'bar',
        data: {
          labels: topOutstanding.map(item => item[0]),
          datasets: [
            {
              label: 'Outstanding Balance (LKR)',
              data: topOutstanding.map(item => item[1]),
              backgroundColor: topOutstanding.map((item, index) => {
                const colors = [
                  'linear-gradient(135deg, rgba(239, 68, 68, 0.9) 0%, rgba(239, 68, 68, 0.7) 100%)',
                  'linear-gradient(135deg, rgba(245, 158, 11, 0.9) 0%, rgba(245, 158, 11, 0.7) 100%)',
                  'linear-gradient(135deg, rgba(59, 130, 246, 0.9) 0%, rgba(59, 130, 246, 0.7) 100%)',
                  'linear-gradient(135deg, rgba(16, 185, 129, 0.9) 0%, rgba(16, 185, 129, 0.7) 100%)',
                  'linear-gradient(135deg, rgba(139, 92, 246, 0.9) 0%, rgba(139, 92, 246, 0.7) 100%)',
                  'linear-gradient(135deg, rgba(217, 70, 239, 0.9) 0%, rgba(217, 70, 239, 0.7) 100%)',
                  'linear-gradient(135deg, rgba(14, 165, 233, 0.9) 0%, rgba(14, 165, 233, 0.7) 100%)',
                  'linear-gradient(135deg, rgba(251, 146, 60, 0.9) 0%, rgba(251, 146, 60, 0.7) 100%)'
                ];
                return colors[index % colors.length];
              }),
              borderColor: [
                'rgb(239, 68, 68)',
                'rgb(245, 158, 11)',
                'rgb(59, 130, 246)',
                'rgb(16, 185, 129)',
                'rgb(139, 92, 246)',
                'rgb(217, 70, 239)',
                'rgb(14, 165, 233)',
                'rgb(251, 146, 60)'
              ].slice(0, topOutstanding.length),
              borderWidth: 1,
              borderRadius: 8,
              borderSkipped: false,
            }
          ]
        },
        title: 'Top Outstanding Balances',
        subtitle: topOutstanding.length > 0 ? `${topOutstanding.length} clients with outstanding balances` : 'No outstanding balances',
        icon: Wallet, // Changed from DollarSign to Wallet
        color: '#EF4444',
        emptyMessage: 'No outstanding balances found'
      },
      
      paymentsChart: {
        type: 'line',
        data: {
          labels: last6Months,
          datasets: [
            {
              label: 'Payment Collection (LKR)',
              data: last6Months.map(month => monthlyRevenue[month] || 0),
              borderColor: '#6366F1',
              backgroundColor: 'linear-gradient(180deg, rgba(99, 102, 241, 0.2) 0%, rgba(99, 102, 241, 0.05) 100%)',
              tension: 0.4,
              fill: true,
              pointRadius: 6,
              pointHoverRadius: 10,
              pointBackgroundColor: '#6366F1',
              pointBorderColor: '#FFFFFF',
              pointBorderWidth: 2,
            }
          ]
        },
        title: 'Monthly Payment Trend',
        subtitle: 'Last 6 months payment collection',
        icon: TrendingUp,
        color: '#6366F1',
        emptyMessage: 'No payment history available'
      },
      
      licenseChart: {
        type: 'doughnut',
        data: licenseStatusData,
        title: 'License Expiry Status',
        subtitle: `${stats.totalLicenses} total licenses`,
        icon: Shield,
        color: '#10B981',
        emptyMessage: 'No license data available'
      },
      
      interactionsChart: {
        type: 'polarArea',
        data: {
          labels: interactionTypesArray.map(item => item[0]),
          datasets: [
            {
              label: 'Business Interactions',
              data: interactionTypesArray.map(item => item[1]),
              backgroundColor: [
                'rgba(239, 68, 68, 0.8)',
                'rgba(245, 158, 11, 0.8)',
                'rgba(59, 130, 246, 0.8)',
                'rgba(16, 185, 129, 0.8)',
                'rgba(139, 92, 246, 0.8)',
                'rgba(236, 72, 153, 0.8)',
                'rgba(20, 184, 166, 0.8)',
                'rgba(245, 158, 11, 0.8)'
              ].slice(0, interactionTypesArray.length),
              borderColor: [
                'rgb(239, 68, 68)',
                'rgb(245, 158, 11)',
                'rgb(59, 130, 246)',
                'rgb(16, 185, 129)',
                'rgb(139, 92, 246)',
                'rgb(236, 72, 153)',
                'rgb(20, 184, 166)',
                'rgb(245, 158, 11)'
              ].slice(0, interactionTypesArray.length),
              borderWidth: 2,
            }
          ]
        },
        title: 'Business Interaction Types',
        subtitle: `${stats.totalLogs} total interactions`,
        icon: Activity,
        color: '#EC4899',
        emptyMessage: 'No business interactions logged'
      }
    };
  };

  const chartData = prepareChartData();

  // Chart options
  const barOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false
      },
      tooltip: {
        backgroundColor: 'rgba(17, 24, 39, 0.9)',
        titleColor: '#F9FAFB',
        bodyColor: '#F9FAFB',
        borderColor: '#4B5563',
        borderWidth: 1,
        padding: 12,
        cornerRadius: 8,
        callbacks: {
          label: function(context) {
            return `${context.dataset.label}: ${formatCurrency(context.raw)}`;
          }
        }
      }
    },
    scales: {
      y: {
        beginAtZero: true,
        grid: {
          color: "#E5E7EB",
          drawBorder: false,
        },
        ticks: {
          callback: function(value) {
            return formatCurrency(value);
          },
          font: {
            family: "'Inter', sans-serif"
          }
        }
      },
      x: {
        grid: {
          display: false,
        },
        ticks: {
          maxRotation: 45,
          minRotation: 45,
          font: {
            family: "'Inter', sans-serif"
          }
        }
      },
    },
  };

  const lineOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false
      },
      tooltip: {
        backgroundColor: 'rgba(17, 24, 39, 0.9)',
        titleColor: '#F9FAFB',
        bodyColor: '#F9FAFB',
        borderColor: '#4B5563',
        borderWidth: 1,
        padding: 12,
        cornerRadius: 8,
        callbacks: {
          label: function(context) {
            return `${context.dataset.label}: ${formatCurrency(context.raw)}`;
          }
        }
      }
    },
    scales: {
      y: {
        beginAtZero: true,
        grid: {
          color: "#E5E7EB",
          drawBorder: false,
        },
        ticks: {
          callback: function(value) {
            return formatCurrency(value);
          },
          font: {
            family: "'Inter', sans-serif"
          }
        }
      },
      x: {
        grid: {
          color: "#E5E7EB",
          drawBorder: false,
        },
        ticks: {
          font: {
            family: "'Inter', sans-serif"
          }
        },
      },
    },
  };

  const doughnutOptions = {
    responsive: true,
    maintainAspectRatio: false,
    cutout: '65%',
    plugins: {
      legend: {
        position: 'right',
        labels: {
          padding: 20,
          usePointStyle: true,
          pointStyle: 'circle',
          font: {
            family: "'Inter', sans-serif",
            size: 12
          }
        }
      },
      tooltip: {
        backgroundColor: 'rgba(17, 24, 39, 0.9)',
        titleColor: '#F9FAFB',
        bodyColor: '#F9FAFB',
        borderColor: '#4B5563',
        borderWidth: 1,
        padding: 12,
        cornerRadius: 8,
        callbacks: {
          label: function(context) {
            const total = context.dataset.data.reduce((a, b) => a + b, 0);
            const percentage = total > 0 ? ((context.raw / total) * 100).toFixed(1) : 0;
            return `${context.label}: ${context.raw} (${percentage}%)`;
          }
        }
      }
    }
  };

  const polarOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'right',
        labels: {
          padding: 15,
          font: {
            family: "'Inter', sans-serif",
            size: 12
          }
        }
      },
      tooltip: {
        backgroundColor: 'rgba(17, 24, 39, 0.9)',
        titleColor: '#F9FAFB',
        bodyColor: '#F9FAFB',
        borderColor: '#4B5563',
        borderWidth: 1,
        padding: 12,
        cornerRadius: 8,
      }
    },
    scales: {
      r: {
        ticks: {
          display: false
        },
        grid: {
          color: '#E5E7EB',
          circular: true
        },
        angleLines: {
          color: '#E5E7EB'
        }
      }
    }
  };

  // Handle chart click
  const handleChartClick = (chartKey) => {
    setSelectedChart(chartKey);
    setChartViewTitle(chartData[chartKey].title);
    setCurrentPage(1);
    
    switch (chartKey) {
      case 'outstandingChart':
        setChartViewData(dashboardData.outstandingBalance);
        break;
      case 'paymentsChart':
        setChartViewData(dashboardData.paymentHistory);
        break;
      case 'licenseChart':
        setChartViewData(dashboardData.expiringLicenses);
        break;
      case 'interactionsChart':
        setChartViewData(dashboardData.businessLogs);
        break;
      default:
        setChartViewData([]);
    }
    
    setShowChartDetail(true);
  };

  // Pagination functions
  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handleItemsPerPageChange = (value) => {
    setItemsPerPage(parseInt(value));
    setCurrentPage(1);
  };

  const getPaginatedData = () => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return chartViewData.slice(startIndex, endIndex);
  };

  const totalPages = Math.ceil(chartViewData.length / itemsPerPage) || 1;

  // Export data functions - Only Excel
  const exportToExcel = (data, fileName) => {
    setIsExporting(true);
    try {
      const worksheet = XLSX.utils.json_to_sheet(data);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Data");
      XLSX.writeFile(workbook, `${fileName}_${new Date().toISOString().split('T')[0]}.xlsx`);
    } catch (error) {
      console.error('Error exporting to Excel:', error);
      alert('Error exporting data. Please try again.');
    } finally {
      setIsExporting(false);
    }
  };

  const handleExport = () => {
    if (!selectedChart || chartViewData.length === 0) {
      alert('No data to export');
      return;
    }

    const fileName = `${chartViewTitle.replace(/\s+/g, '_')}_Data`;
    const dataToExport = chartViewData;
    exportToExcel(dataToExport, fileName);
  };

  // Render chart component based on type
  const renderChart = (chartKey) => {
    const chart = chartData[chartKey];
    const Icon = chart.icon;
    const hasData = chartKey === 'outstandingChart' ? 
      dashboardData.outstandingBalance.length > 0 :
      chartKey === 'paymentsChart' ? dashboardData.paymentHistory.length > 0 :
      chartKey === 'licenseChart' ? dashboardData.expiringLicenses.length > 0 :
      chartKey === 'interactionsChart' ? dashboardData.businessLogs.length > 0 :
      false;

    const ChartComponent = {
      'bar': Bar,
      'line': Line,
      'doughnut': Doughnut,
      'polarArea': PolarArea
    }[chart.type];

    const chartOptions = {
      'bar': barOptions,
      'line': lineOptions,
      'doughnut': doughnutOptions,
      'polarArea': polarOptions
    };

    return (
      <div className="bg-gradient-to-br from-white to-gray-50 rounded-xl md:rounded-2xl shadow-lg md:shadow-xl p-4 md:p-6 border border-gray-200 hover:shadow-xl md:hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-1">
        <div className="flex flex-col md:flex-row md:items-center justify-between mb-4 md:mb-6 gap-4 md:gap-0">
          <div className="flex items-center space-x-3 md:space-x-4">
            <div className="p-2 md:p-3 rounded-lg md:rounded-xl shadow-md" style={{ 
              background: `linear-gradient(135deg, ${chart.color}20 0%, ${chart.color}10 100%)`,
              border: `1px solid ${chart.color}30`
            }}>
              <Icon className="h-5 w-5 md:h-7 md:w-7" style={{ color: chart.color }} />
            </div>
            <div>
              <h3 className="text-lg md:text-xl font-bold text-gray-800">{chart.title}</h3>
              <p className="text-xs md:text-sm text-gray-600 mt-1">{chart.subtitle}</p>
            </div>
          </div>
          <button
            onClick={() => handleChartClick(chartKey)}
            className={`flex items-center justify-center space-x-2 px-3 md:px-4 py-1.5 md:py-2 rounded-lg md:rounded-xl transition-all duration-300 ${hasData 
                ? 'bg-gradient-to-r from-blue-500 to-blue-600 text-white hover:from-blue-600 hover:to-blue-700 shadow-md hover:shadow-lg' 
                : 'bg-gray-100 text-gray-400 cursor-not-allowed'}`}
            disabled={!hasData}
            title={hasData ? 'View detailed data' : 'No data available'}
          >
            <Eye size={16} className="md:hidden" />
            <Eye size={18} className="hidden md:block" />
            <span className="text-xs md:text-sm font-medium">View Data</span>
          </button>
        </div>
        
        {hasData ? (
          <div className="h-48 md:h-64 lg:h-72">
            <ChartComponent 
              ref={chartRefs[chartKey]}
              data={chart.data} 
              options={chartOptions[chart.type]} 
            />
          </div>
        ) : (
          <div className="h-48 md:h-64 lg:h-72 flex flex-col items-center justify-center bg-gradient-to-br from-gray-50 to-gray-100 rounded-lg md:rounded-xl">
            <Database className="h-10 w-10 md:h-16 md:w-16 text-gray-300 mb-3 md:mb-4" />
            <p className="text-gray-500 font-medium text-sm md:text-lg">{chart.emptyMessage}</p>
            <p className="text-xs md:text-sm text-gray-400 mt-1 md:mt-2">Try refreshing the data</p>
          </div>
        )}
      </div>
    );
  };

  // Stats Cards Component
  const StatCard = ({ title, value, icon: Icon, color, loading: isLoading, isCurrency = false }) => (
    <div className="bg-gradient-to-br from-white to-gray-50 rounded-xl md:rounded-2xl shadow-lg p-4 md:p-6 border border-gray-200 hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1">
      <div className="flex items-center justify-between">
        <div className="flex-1 min-w-0 mr-3 md:mr-4">
          <h3 className="text-xs md:text-sm font-semibold text-gray-600 mb-1 md:mb-2 uppercase tracking-wider truncate">{title}</h3>
          <div className="flex items-baseline">
            {isCurrency && <span className="text-xs md:text-base text-gray-500 mr-1 md:mr-2">LKR</span>}
            <p className="text-xl md:text-2xl lg:text-3xl font-bold text-gray-800 truncate">
              {isLoading ? (
                <div className="animate-pulse bg-gradient-to-r from-gray-200 to-gray-300 h-6 md:h-8 lg:h-10 w-16 md:w-20 lg:w-24 rounded-lg"></div>
              ) : isCurrency ? (
                new Intl.NumberFormat('en-US', {
                  minimumFractionDigits: 0,
                  maximumFractionDigits: 0
                }).format(value)
              ) : (
                value
              )}
            </p>
          </div>
        </div>
        <div className="flex-shrink-0 p-2 md:p-3 lg:p-4 rounded-lg md:rounded-xl shadow-md" style={{ 
          background: `linear-gradient(135deg, ${color}20 0%, ${color}10 100%)`,
          border: `1px solid ${color}30`,
          minWidth: '48px',
          minHeight: '48px'
        }}>
          <Icon className="h-5 w-5 md:h-6 md:w-6 lg:h-7 lg:w-7" style={{ color: color }} />
        </div>
      </div>
    </div>
  );

  // Render detailed table for selected chart
  const renderChartDetailTable = () => {
    if (!selectedChart || chartViewData.length === 0) return null;

    const columns = getTableColumns(selectedChart);
    const paginatedData = getPaginatedData();
    
    return (
      <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-2 md:p-4 backdrop-blur-sm">
        <div className="bg-gradient-to-br from-white to-gray-50 rounded-xl md:rounded-2xl lg:rounded-3xl shadow-xl md:shadow-2xl w-full max-w-full md:max-w-4xl lg:max-w-6xl xl:max-w-7xl max-h-[85vh] md:max-h-[90vh] flex flex-col">
          {/* Header */}
          <div className="px-4 md:px-6 lg:px-8 py-4 md:py-6 border-b border-gray-200 bg-gradient-to-r from-gray-50 to-white">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-lg md:text-xl lg:text-2xl font-bold text-gray-800">{chartViewTitle}</h2>
                <p className="text-gray-600 mt-1 text-xs md:text-sm">
                  Showing {Math.min(itemsPerPage, chartViewData.length)} of {chartViewData.length} records • Page {currentPage} of {totalPages}
                </p>
              </div>
              <button
                onClick={() => setShowChartDetail(false)}
                className="p-2 md:p-3 hover:bg-gray-100 rounded-full transition-colors hover:rotate-90 duration-300"
              >
                <X size={20} className="text-gray-500" />
              </button>
            </div>
          </div>

          {/* Toolbar */}
          <div className="px-4 md:px-6 lg:px-8 py-3 md:py-4 border-b border-gray-200 bg-white">
            <div className="flex flex-col md:flex-row flex-wrap items-start md:items-center justify-between gap-3 md:gap-4">
              <div className="flex items-center space-x-2 md:space-x-3 bg-gray-100 px-3 md:px-4 py-1.5 md:py-2 rounded-lg md:rounded-xl">
                <Filter size={16} className="text-gray-600" />
                <span className="text-xs md:text-sm text-gray-700 font-medium">Export Options</span>
              </div>
              
              <div className="flex flex-col md:flex-row items-start md:items-center gap-3 md:gap-4 w-full md:w-auto">
                <div className="flex items-center space-x-2">
                  <span className="text-xs md:text-sm text-gray-700 font-medium">Rows per page:</span>
                  <select
                    value={itemsPerPage}
                    onChange={(e) => handleItemsPerPageChange(e.target.value)}
                    className="px-2 md:px-3 py-1 md:py-1.5 border border-gray-300 rounded-lg text-xs md:text-sm"
                  >
                    <option value="10">10</option>
                    <option value="25">25</option>
                    <option value="50">50</option>
                    <option value="100">100</option>
                  </select>
                </div>
                
                <button
                  onClick={handleExport}
                  disabled={isExporting || chartViewData.length === 0}
                  className="flex items-center justify-center space-x-2 px-3 md:px-4 lg:px-5 py-1.5 md:py-2 lg:py-2.5 bg-gradient-to-r from-green-500 to-emerald-600 text-white rounded-lg md:rounded-xl hover:from-green-600 hover:to-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-300 transform hover:-translate-y-0.5 shadow-md hover:shadow-lg w-full md:w-auto"
                >
                  {isExporting ? (
                    <RefreshCw size={16} className="animate-spin" />
                  ) : (
                    <Download size={16} />
                  )}
                  <span className="text-xs md:text-sm font-medium">Export Excel</span>
                </button>
              </div>
            </div>
          </div>

          {/* Table */}
          <div className="flex-1 overflow-auto" style={{ maxHeight: '300px', minHeight: '200px' }}>
            {chartViewData.length > 0 ? (
              <div className="relative min-w-[800px]">
                <table className="w-full">
                  <thead className="bg-gradient-to-r from-gray-50 to-gray-100 sticky top-0 z-10">
                    <tr>
                      <th className="px-3 md:px-4 lg:px-6 py-2 md:py-3 lg:py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider border-b border-gray-300">
                        S.No
                      </th>
                      {columns.map((col, index) => (
                        <th key={index} className="px-3 md:px-4 lg:px-6 py-2 md:py-3 lg:py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider border-b border-gray-300">
                          <div className="flex items-center space-x-1 md:space-x-2">
                            <span>{col.label}</span>
                            {col.isSortable && <ChevronDown size={12} className="text-gray-500 hidden md:block" />}
                          </div>
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {paginatedData.map((item, rowIndex) => {
                      const serialNumber = (currentPage - 1) * itemsPerPage + rowIndex + 1;
                      
                      return (
                        <tr key={rowIndex} className="hover:bg-gradient-to-r hover:from-blue-50 hover:to-blue-100 transition-all duration-200">
                          <td className="px-3 md:px-4 lg:px-6 py-2 md:py-3 lg:py-4 whitespace-nowrap text-xs md:text-sm text-gray-900 font-medium">
                            {serialNumber}
                          </td>
                          {columns.map((col, colIndex) => (
                            <td key={colIndex} className="px-3 md:px-4 lg:px-6 py-2 md:py-3 lg:py-4 whitespace-nowrap text-xs md:text-sm text-gray-900">
                              {renderTableCell(item, col)}
                            </td>
                          ))}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center h-48 md:h-64">
                <FileText className="h-10 w-10 md:h-16 md:w-16 text-gray-300 mb-3 md:mb-4" />
                <p className="text-gray-500 font-medium text-sm md:text-lg">No data available</p>
                <p className="text-gray-400 text-xs md:text-sm mt-1 md:mt-2">Try refreshing the data</p>
              </div>
            )}
          </div>

          {/* Footer with pagination */}
          <div className="px-4 md:px-6 lg:px-8 py-3 md:py-4 border-t border-gray-200 bg-gradient-to-r from-gray-50 to-gray-100">
            <div className="flex flex-col md:flex-row items-center justify-between text-xs md:text-sm text-gray-700 gap-3 md:gap-0">
              <div className="flex items-center space-x-2 md:space-x-4">
                <span className="font-medium">Total: {chartViewData.length.toLocaleString()} records</span>
              </div>
              <div className="flex flex-col md:flex-row items-center space-y-3 md:space-y-0 md:space-x-4 lg:space-x-6">
                <div className="flex items-center space-x-2">
                  <span className="font-medium">Page</span>
                  <input
                    type="number"
                    min="1"
                    max={totalPages}
                    value={currentPage}
                    onChange={(e) => {
                      const page = Math.min(Math.max(1, parseInt(e.target.value) || 1), totalPages);
                      handlePageChange(page);
                    }}
                    className="w-12 md:w-16 px-2 py-1 border border-gray-300 rounded text-center text-xs md:text-sm"
                  />
                  <span className="font-medium">of {totalPages}</span>
                </div>
                <div className="flex items-center space-x-1 md:space-x-2">
                  <button
                    onClick={() => handlePageChange(Math.max(1, currentPage - 1))}
                    disabled={currentPage === 1}
                    className="p-1.5 md:p-2 hover:bg-gray-200 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-sm md:text-base"
                  >
                    ←
                  </button>
                  {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                    let pageNum;
                    if (totalPages <= 5) {
                      pageNum = i + 1;
                    } else if (currentPage <= 3) {
                      pageNum = i + 1;
                    } else if (currentPage >= totalPages - 2) {
                      pageNum = totalPages - 4 + i;
                    } else {
                      pageNum = currentPage - 2 + i;
                    }
                    
                    return (
                      <button
                        key={pageNum}
                        onClick={() => handlePageChange(pageNum)}
                        className={`px-2 md:px-3 py-1 rounded-lg transition-colors text-xs md:text-sm ${currentPage === pageNum
                            ? 'bg-blue-500 text-white'
                            : 'hover:bg-gray-200'
                        }`}
                      >
                        {pageNum}
                      </button>
                    );
                  })}
                  <button
                    onClick={() => handlePageChange(Math.min(totalPages, currentPage + 1))}
                    disabled={currentPage === totalPages}
                    className="p-1.5 md:p-2 hover:bg-gray-200 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-sm md:text-base"
                  >
                    →
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Helper function to get table columns based on chart type
  const getTableColumns = (chartKey) => {
    switch (chartKey) {
      case 'outstandingChart':
        return [
          { key: 'scm_client_name', label: 'Client Name', isSortable: true },
          { key: 'sbm_business_name', label: 'Business Name', isSortable: true },
          { key: 'ssm_system_name', label: 'System Name', isSortable: true },
          { key: 'slm_license_key', label: 'License Key', isSortable: true },
          { key: 'spm_invoice_no', label: 'Invoice No', isSortable: true },
          { key: 'spm_total_amount', label: 'Total Amount', isCurrency: true, isSortable: true },
          { key: 'spm_paid_amount', label: 'Paid Amount', isCurrency: true, isSortable: true },
          { key: 'spm_balance_amount', label: 'Balance Amount', isCurrency: true, isSortable: true },
          { key: 'payment_status', label: 'Payment Status', isStatus: true, isSortable: true }
        ];
      case 'paymentsChart':
        return [
          { key: 'scm_client_name', label: 'Client Name', isSortable: true },
          { key: 'sbm_business_name', label: 'Business Name', isSortable: true },
          { key: 'ssm_system_name', label: 'System Name', isSortable: true },
          { key: 'slm_license_key', label: 'License Key', isSortable: true },
          { key: 'spd_payment_date', label: 'Payment Date', isDate: true, isSortable: true },
          { key: 'spd_amount', label: 'Amount', isCurrency: true, isSortable: true },
          { key: 'spd_payment_method', label: 'Payment Method', isSortable: true },
          { key: 'spd_reference_no', label: 'Reference No', isSortable: true },
          { key: 'spd_remarks', label: 'Remarks', isSortable: true }
        ];
      case 'licenseChart':
        return [
          { key: 'scm_client_name', label: 'Client Name', isSortable: true },
          { key: 'sbm_business_name', label: 'Business Name', isSortable: true },
          { key: 'ssm_system_name', label: 'System Name', isSortable: true },
          { key: 'slm_license_key', label: 'License Key', isSortable: true },
          { key: 'slm_expiry_date', label: 'Expiry Date', isDate: true, isSortable: true },
          { key: 'status', label: 'Status', isStatus: true, isSortable: true }
        ];
      case 'interactionsChart':
        return [
          { key: 'scm_client_name', label: 'Client Name', isSortable: true },
          { key: 'sbm_business_name', label: 'Business Name', isSortable: true },
          { key: 'sbl_log_type', label: 'Log Type', isSortable: true },
          { key: 'sbl_log_subject', label: 'Subject', isSortable: true },
          { key: 'sbl_log_notes', label: 'Notes', isSortable: true },
          { key: 'sbl_log_date', label: 'Log Date', isDate: true, isSortable: true }
        ];
      default:
        return [];
    }
  };

  // Helper function to render table cell
  const renderTableCell = (item, column) => {
    const value = item[column.key] || 'N/A';
    
    if (column.isCurrency) {
      return (
        <div className="font-semibold text-gray-800 text-xs md:text-sm">
          {formatCurrency(value)}
        </div>
      );
    }
    
    if (column.isDate) {
      return (
        <div className="flex items-center space-x-1 md:space-x-2">
          <Calendar size={12} className="text-gray-400 hidden md:block" />
          <span className="text-xs md:text-sm">{formatDate(value)}</span>
        </div>
      );
    }
    
    if (column.isStatus) {
      let statusClass = '';
      let statusText = value;
      
      if (column.key === 'payment_status') {
        switch(value?.toLowerCase()) {
          case 'paid':
            statusClass = 'bg-gradient-to-r from-green-100 to-green-50 text-green-800 border border-green-200';
            break;
          case 'partially paid':
            statusClass = 'bg-gradient-to-r from-yellow-100 to-yellow-50 text-yellow-800 border border-yellow-200';
            break;
          case 'unpaid':
            statusClass = 'bg-gradient-to-r from-red-100 to-red-50 text-red-800 border border-red-200';
            break;
          default:
            statusClass = 'bg-gradient-to-r from-gray-100 to-gray-50 text-gray-800 border border-gray-200';
        }
      } else if (column.key === 'status') {
        const days = getDaysUntilExpiry(item.slm_expiry_date || item.expiry_date);
        if (days <= 0 && days !== -1) {
          statusClass = 'bg-gradient-to-r from-gray-100 to-gray-50 text-gray-800 border border-gray-200';
          statusText = 'Expired';
        } else if (days <= 7) {
          statusClass = 'bg-gradient-to-r from-red-100 to-red-50 text-red-800 border border-red-200';
          statusText = 'Urgent';
        } else if (days <= 30) {
          statusClass = 'bg-gradient-to-r from-yellow-100 to-yellow-50 text-yellow-800 border border-yellow-200';
          statusText = 'Expiring Soon';
        } else if (days > 30) {
          statusClass = 'bg-gradient-to-r from-green-100 to-green-50 text-green-800 border border-green-200';
          statusText = 'Active';
        } else {
          statusClass = 'bg-gradient-to-r from-gray-100 to-gray-50 text-gray-800 border border-gray-200';
          statusText = 'Unknown';
        }
      }
      
      return (
        <span className={`px-2 md:px-3 py-0.5 md:py-1 rounded-full text-xs font-semibold ${statusClass}`}>
          {statusText}
        </span>
      );
    }
    
    if (typeof value === 'string' && value.length > 30) {
      return (
        <div className="group relative">
          <span className="truncate block max-w-[100px] md:max-w-[150px] lg:max-w-xs text-xs md:text-sm">{value.substring(0, 30)}...</span>
          <div className="absolute left-0 top-full mt-1 p-2 bg-gray-900 text-white text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity z-10 whitespace-normal w-48 md:w-64 hidden md:block">
            {value}
          </div>
        </div>
      );
    }
    
    return <span className="text-xs md:text-sm">{value}</span>;
  };

  return (
    <div className="p-3 md:p-4 lg:p-6">
      {/* Header */}
      <div className="mb-6 md:mb-8">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center space-y-4 md:space-y-0">
          <div>
            <h1 className="text-xl md:text-2xl lg:text-3xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">Dashboard Overview</h1>
            <p className="text-gray-600 mt-1 md:mt-2 text-sm md:text-base">Monitor your license management system performance and metrics</p>
          </div>
          <div className="flex items-center space-x-2 md:space-x-4">
            <button
              onClick={loadDashboardData}
              disabled={Object.values(loading).some(l => l)}
              className="flex items-center justify-center space-x-2 px-4 md:px-6 py-2 md:py-3 bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded-lg md:rounded-xl hover:from-blue-600 hover:to-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-300 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 w-full md:w-auto"
            >
              <RefreshCw size={16} className={Object.values(loading).some(l => l) ? 'animate-spin' : ''} />
              <span className="text-sm md:text-base font-semibold">Refresh Data</span>
            </button>
          </div>
        </div>
      </div>

      {/* Quick Stats - Only 4 boxes */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6 mb-6 md:mb-8">
        <StatCard
          title="Total Clients"
          value={stats.totalClients}
          icon={Users}
          color="#8B5CF6"
          loading={loading.clients}
        />
        
        <StatCard
          title="Total Outstanding"
          value={stats.outstandingAmount}
          icon={Wallet} 
          color="#EF4444"
          loading={loading.outstanding}
          isCurrency={true}
        />
        
        <StatCard
          title="Total Payments"
          value={stats.totalPayments}
          icon={CreditCard}
          color="#10B981"
          loading={loading.payments}
          isCurrency={true}
        />
        
        <StatCard
          title="License Expiry Status"
          value={`${stats.activeLicenses}/${stats.totalLicenses}`}
          icon={Shield}
          color="#3B82F6"
          loading={loading.expiring}
        />
      </div>

      {/* Charts Grid */}
      <div className="mb-8 md:mb-10">
        <div className="flex flex-col md:flex-row items-start md:items-center justify-between mb-4 md:mb-6 gap-3 md:gap-0">
          <h2 className="text-lg md:text-xl lg:text-2xl font-bold text-gray-800">Performance Analytics</h2>
          <div className="flex items-center space-x-2 md:space-x-3 bg-gradient-to-r from-blue-50 to-purple-50 px-3 md:px-4 py-1.5 md:py-2 rounded-lg md:rounded-xl">
            <BarChart3 className="text-blue-500" size={18} />
            <span className="text-xs md:text-sm text-gray-700 font-medium">4 Visual Charts</span>
          </div>
        </div>
        
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 md:gap-8">
          {/* Top Outstanding Balances */}
          {renderChart('outstandingChart')}
          
          {/* Payment Trend */}
          {renderChart('paymentsChart')}
          
          {/* License Status */}
          {renderChart('licenseChart')}
          
          {/* Business Interactions */}
          {renderChart('interactionsChart')}
        </div>
      </div>

      {/* API Status */}
      <div className="bg-gradient-to-br from-white to-gray-50 rounded-xl md:rounded-2xl shadow-lg md:shadow-xl p-4 md:p-6 mb-6 md:mb-8 border border-gray-200">
        <h3 className="text-lg md:text-xl font-bold text-gray-800 mb-4 md:mb-6 flex items-center space-x-2 md:space-x-3">
          <div className="p-2 bg-gradient-to-r from-blue-100 to-blue-50 rounded-lg">
            <Database className="h-5 w-5 md:h-6 md:w-6 text-blue-500" />
          </div>
          <span>Data Sources Status</span>
        </h3>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3 md:gap-4">
          {[
            { 
              key: 'clients', 
              label: 'Total Clients', 
              data: dashboardData.clients,
              loading: loading.clients,
              value: stats.totalClients,
              isCurrency: false
            },
            { 
              key: 'outstanding', 
              label: 'Outstanding Balance', 
              data: dashboardData.outstandingBalance,
              loading: loading.outstanding,
              value: stats.outstandingAmount,
              isCurrency: true
            },
            { 
              key: 'payments', 
              label: 'Payment History', 
              data: dashboardData.paymentHistory,
              loading: loading.payments,
              value: stats.totalPayments,
              isCurrency: true
            },
            { 
              key: 'expiring', 
              label: 'License Data', 
              data: dashboardData.expiringLicenses,
              loading: loading.expiring,
              value: stats.activeLicenses,
              isCurrency: false
            },
            { 
              key: 'logs', 
              label: 'Business Logs', 
              data: dashboardData.businessLogs,
              loading: loading.logs,
              value: stats.totalLogs,
              isCurrency: false
            }
          ].map((source) => (
            <div 
              key={source.key} 
              className={`p-3 md:p-4 rounded-lg md:rounded-xl border transition-all duration-300 transform hover:-translate-y-1 ${source.loading 
                  ? 'border-yellow-300 bg-gradient-to-br from-yellow-50 to-yellow-100' 
                  : source.data.length > 0 
                    ? 'border-green-300 bg-gradient-to-br from-green-50 to-green-100' 
                    : 'border-gray-300 bg-gradient-to-br from-gray-50 to-gray-100'}`}
            >
              <div className="flex items-center justify-between mb-2 md:mb-3">
                <span className="font-medium text-gray-800 text-xs md:text-sm">{source.label}</span>
                <div className={`w-2 h-2 md:w-3 md:h-3 rounded-full ${source.loading 
                    ? 'bg-yellow-500 animate-pulse' 
                    : source.data.length > 0 
                      ? 'bg-green-500' 
                      : 'bg-gray-400'}`} />
              </div>
              <div className="text-sm md:text-lg font-bold text-gray-900 mb-1">
                {source.loading ? '...' : source.data.length}
                <span className="text-xs font-normal text-gray-600 ml-1">record(s)</span>
              </div>
              {source.data.length > 0 && (
                <div className="text-xs md:text-sm text-gray-700">
                  Total: {source.isCurrency ? formatCurrency(source.value) : source.value}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Chart Detail Modal */}
      {showChartDetail && renderChartDetailTable()}

      {/* Loading Overlay */}
      {Object.values(loading).some(l => l) && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 backdrop-blur-sm">
          <div className="bg-gradient-to-br from-white to-gray-50 rounded-xl md:rounded-2xl p-6 md:p-8 lg:p-10 flex flex-col items-center shadow-xl md:shadow-2xl transform -translate-y-2 md:-translate-y-4">
            <div className="relative">
              <div className="absolute inset-0 bg-gradient-to-r from-blue-500 to-purple-500 opacity-20 rounded-full blur-xl"></div>
              <RefreshCw className="h-10 w-10 md:h-16 md:w-16 text-blue-500 animate-spin mb-4 md:mb-6" />
            </div>
            <p className="text-gray-800 font-semibold text-lg md:text-xl mt-3 md:mt-4">Loading dashboard data...</p>
            <p className="text-gray-600 mt-1 md:mt-2 text-sm md:text-base">Fetching data from all APIs</p>
            <div className="mt-4 md:mt-6 w-48 md:w-64 bg-gray-200 rounded-full h-1.5 md:h-2">
              <div className="bg-gradient-to-r from-blue-500 to-purple-500 h-1.5 md:h-2 rounded-full animate-pulse"></div>
            </div>
          </div>
        </div>
      )}

      {/* Exporting Overlay */}
      {isExporting && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 backdrop-blur-sm">
          <div className="bg-gradient-to-br from-white to-gray-50 rounded-xl md:rounded-2xl p-6 md:p-8 lg:p-10 flex flex-col items-center shadow-xl md:shadow-2xl transform -translate-y-2 md:-translate-y-4">
            <div className="relative">
              <div className="absolute inset-0 bg-gradient-to-r from-green-500 to-emerald-500 opacity-20 rounded-full blur-xl"></div>
              <RefreshCw className="h-10 w-10 md:h-16 md:w-16 text-green-500 animate-spin mb-4 md:mb-6" />
            </div>
            <p className="text-gray-800 font-semibold text-lg md:text-xl mt-3 md:mt-4">Exporting data...</p>
            <p className="text-gray-600 mt-1 md:mt-2 text-sm md:text-base">Preparing Excel file</p>
            <div className="mt-4 md:mt-6 w-48 md:w-64 bg-gray-200 rounded-full h-1.5 md:h-2">
              <div className="bg-gradient-to-r from-green-500 to-emerald-500 h-1.5 md:h-2 rounded-full animate-pulse"></div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Dashboard;